<script lang="ts">
  import {
    type GameraTeamReference,
    getValue,
    getDisplay,
    type FieldGoalEvent,
    type FreeThrowEvent,
  } from '@statmuse/core/gamera'
  import TeamPanel from '../common/team-panel.svelte'
  import { teamStats, plays } from './stores'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined

  const points = (x: FieldGoalEvent | FreeThrowEvent) => {
    if (x.type === 'freeThrow') return 1
    if (x.isThreePoint) return 3
    return 2
  }

  $: awayTeamStats =
    $teamStats.away?.statsLookup[$teamStats.away?.statsLookupKey ?? '']
  $: homeTeamStats =
    $teamStats.home?.statsLookup[$teamStats.home?.statsLookupKey ?? '']

  $: biggestLead = $plays
    .filter((p) => p.type !== 'legacyFieldGoal')
    .reduce(
      (acc, e) => {
        return {
          away: Math.max(acc.away, e.awayPoints - e.homePoints),
          home: Math.max(acc.home, e.homePoints - e.awayPoints),
        }
      },
      { away: 0, home: 0 },
    )

  $: ties = $plays.filter(
    (p) =>
      (p.type === 'fieldGoal' || p.type === 'freeThrow') &&
      p.isMade &&
      p.awayPoints === p.homePoints,
  ).length

  $: leadChanges = $plays
    .filter(
      (p): p is FieldGoalEvent | FreeThrowEvent =>
        (p.type === 'fieldGoal' || p.type === 'freeThrow') && p.isMade,
    )
    .reduce<{ count: number; currentTeamOnTop?: number }>(
      (acc, p) => {
        const leadDidChange =
          (p.awayPoints > p.homePoints &&
            acc.currentTeamOnTop === homeTeam?.teamId) ||
          (p.homePoints > p.awayPoints &&
            acc.currentTeamOnTop === awayTeam?.teamId)

        return {
          count: leadDidChange ? acc.count + 1 : acc.count,
          currentTeamOnTop:
            p.awayPoints > p.homePoints
              ? awayTeam?.teamId
              : p.homePoints > p.awayPoints
              ? homeTeam?.teamId
              : acc.currentTeamOnTop,
        }
      },
      { count: 0, currentTeamOnTop: undefined },
    )

  $: longestRun = $plays
    .filter(
      (p): p is FieldGoalEvent | FreeThrowEvent =>
        (p.type === 'fieldGoal' || p.type === 'freeThrow') && p.isMade,
    )
    .reduce(
      (acc, p) => {
        const streakEnds = p.teamId !== acc.currentTeam
        const isAwayTeam = p.teamId === awayTeam?.teamId
        const currentRun = streakEnds ? points(p) : points(p) + acc.currentRun

        return {
          away:
            streakEnds && !isAwayTeam
              ? Math.max(acc.away, acc.currentRun)
              : acc.away,
          home:
            streakEnds && isAwayTeam
              ? Math.max(acc.home, acc.currentRun)
              : acc.home,
          currentRun,
          currentTeam: p.teamId,
        }
      },
      { away: 0, home: 0, currentRun: 0, currentTeam: 0 },
    )
</script>

<TeamPanel {awayTeam} {homeTeam} title="Team Stats">
  <div
    class="border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
  >
    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'FieldGoalPercentage') >
        getValue(homeTeamStats ?? {}, 'FieldGoalPercentage')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        <span
          class:text-gray-5={getValue(
            awayTeamStats ?? {},
            'FieldGoalPercentage',
          ) <= getValue(homeTeamStats ?? {}, 'FieldGoalPercentage')}
          class:opacity-50={getValue(
            awayTeamStats ?? {},
            'FieldGoalPercentage',
          ) > getValue(homeTeamStats ?? {}, 'FieldGoalPercentage')}
        >
          {`${getDisplay(awayTeamStats ?? {}, 'FieldGoalsMade')}-${getDisplay(
            awayTeamStats ?? {},
            'FieldGoalsAttempted',
          )}`}
        </span>
        ({getDisplay(awayTeamStats ?? {}, 'FieldGoalPercentage')}%)
      </p>
      <p class="text-gray-5">FG%</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'FieldGoalPercentage') >
        getValue(awayTeamStats ?? {}, 'FieldGoalPercentage')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        <span
          class:text-gray-5={getValue(
            homeTeamStats ?? {},
            'FieldGoalPercentage',
          ) <= getValue(awayTeamStats ?? {}, 'FieldGoalPercentage')}
          class:opacity-50={getValue(
            homeTeamStats ?? {},
            'FieldGoalPercentage',
          ) > getValue(awayTeamStats ?? {}, 'FieldGoalPercentage')}
        >
          {`${getDisplay(homeTeamStats ?? {}, 'FieldGoalsMade')}-${getDisplay(
            homeTeamStats ?? {},
            'FieldGoalsAttempted',
          )}`}
        </span>
        ({getDisplay(homeTeamStats ?? {}, 'FieldGoalPercentage')}%)
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'ThreePointsMade') >
        getValue(homeTeamStats ?? {}, 'ThreePointsMade')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {`${getDisplay(awayTeamStats ?? {}, 'ThreePointsMade')}-${getDisplay(
          awayTeamStats ?? {},
          'ThreePointsAttempted',
        )}`}
        <span
          class:text-gray-5={getValue(awayTeamStats ?? {}, 'ThreePointsMade') <=
            getValue(homeTeamStats ?? {}, 'ThreePointsMade')}
          class:opacity-50={getValue(awayTeamStats ?? {}, 'ThreePointsMade') >
            getValue(homeTeamStats ?? {}, 'ThreePointsMade')}
        >
          ({getDisplay(awayTeamStats ?? {}, 'ThreePointPercentage')}%)
        </span>
      </p>
      <p class="text-gray-5">3PM</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'ThreePointsMade') >
        getValue(awayTeamStats ?? {}, 'ThreePointsMade')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {`${getDisplay(homeTeamStats ?? {}, 'ThreePointsMade')}-${getDisplay(
          homeTeamStats ?? {},
          'ThreePointsAttempted',
        )}`}
        <span
          class:text-gray-5={getValue(homeTeamStats ?? {}, 'ThreePointsMade') <=
            getValue(awayTeamStats ?? {}, 'ThreePointsMade')}
          class:opacity-50={getValue(homeTeamStats ?? {}, 'ThreePointsMade') >
            getValue(awayTeamStats ?? {}, 'ThreePointsMade')}
        >
          ({getDisplay(homeTeamStats ?? {}, 'ThreePointPercentage')}%)
        </span>
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'FreeThrowsMade') >
        getValue(homeTeamStats ?? {}, 'FreeThrowsMade')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {`${getDisplay(awayTeamStats ?? {}, 'FreeThrowsMade')}-${getDisplay(
          awayTeamStats ?? {},
          'FreeThrowsAttempted',
        )}`}
        <span
          class:text-gray-5={getValue(awayTeamStats ?? {}, 'FreeThrowsMade') <=
            getValue(homeTeamStats ?? {}, 'FreeThrowsMade')}
          class:opacity-50={getValue(awayTeamStats ?? {}, 'FreeThrowsMade') >
            getValue(homeTeamStats ?? {}, 'FreeThrowsMade')}
        >
          ({getDisplay(awayTeamStats ?? {}, 'FreeThrowPercentage')}%)
        </span>
      </p>
      <p class="text-gray-5">FTM</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'FreeThrowsMade') >
        getValue(awayTeamStats ?? {}, 'FreeThrowsMade')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {`${getDisplay(homeTeamStats ?? {}, 'FreeThrowsMade')}-${getDisplay(
          homeTeamStats ?? {},
          'FreeThrowsAttempted',
        )}`}
        <span
          class:text-gray-5={getValue(homeTeamStats ?? {}, 'FreeThrowsMade') <=
            getValue(awayTeamStats ?? {}, 'FreeThrowsMade')}
          class:opacity-50={getValue(homeTeamStats ?? {}, 'FreeThrowsMade') >
            getValue(awayTeamStats ?? {}, 'FreeThrowsMade')}
        >
          ({getDisplay(homeTeamStats ?? {}, 'FreeThrowPercentage')}%)
        </span>
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'Rebounds') >
        getValue(homeTeamStats ?? {}, 'Rebounds')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'Rebounds')}
      </p>
      <p class="text-gray-5">Rebounds</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'Rebounds') >
        getValue(awayTeamStats ?? {}, 'Rebounds')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'Rebounds')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'OffensiveRebounds') >
        getValue(homeTeamStats ?? {}, 'OffensiveRebounds')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'OffensiveRebounds')}
      </p>
      <p class="text-gray-5">Offensive Rebounds</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'OffensiveRebounds') >
        getValue(awayTeamStats ?? {}, 'OffensiveRebounds')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'OffensiveRebounds')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'DefensiveRebounds') >
        getValue(homeTeamStats ?? {}, 'DefensiveRebounds')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'DefensiveRebounds')}
      </p>
      <p class="text-gray-5">Defensive Rebounds</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'DefensiveRebounds') >
        getValue(awayTeamStats ?? {}, 'DefensiveRebounds')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'DefensiveRebounds')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'Assists') >
        getValue(homeTeamStats ?? {}, 'Assists')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'Assists')}
      </p>
      <p class="text-gray-5">Assists</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'Assists') >
        getValue(awayTeamStats ?? {}, 'Assists')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'Assists')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'Steals') >
        getValue(homeTeamStats ?? {}, 'Steals')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'Steals')}
      </p>
      <p class="text-gray-5">Steals</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'Steals') >
        getValue(awayTeamStats ?? {}, 'Steals')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'Steals')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'Blocks') >
        getValue(homeTeamStats ?? {}, 'Blocks')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'Blocks')}
      </p>
      <p class="text-gray-5">Blocks</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'Blocks') >
        getValue(awayTeamStats ?? {}, 'Blocks')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'Blocks')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'Turnovers') <
        getValue(homeTeamStats ?? {}, 'Turnovers')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'Turnovers')}
      </p>
      <p class="text-gray-5">Turnovers</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'Turnovers') <
        getValue(awayTeamStats ?? {}, 'Turnovers')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'Turnovers')}
      </p>
    </div>
  </div>
</TeamPanel>

<TeamPanel>
  <div
    class=" border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
  >
    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'PointsOffTurnovers') >
        getValue(homeTeamStats ?? {}, 'PointsOffTurnovers')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'PointsOffTurnovers')}
      </p>
      <p class="text-gray-5">Points Off Turnovers</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'PointsOffTurnovers') >
        getValue(awayTeamStats ?? {}, 'PointsOffTurnovers')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'PointsOffTurnovers')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'FastBreakPoints') >
        getValue(homeTeamStats ?? {}, 'FastBreakPoints')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'FastBreakPoints')}
      </p>
      <p class="text-gray-5">Fastbreak Points</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'FastBreakPoints') >
        getValue(awayTeamStats ?? {}, 'FastBreakPoints')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'FastBreakPoints')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'PointsInPaint') >
        getValue(homeTeamStats ?? {}, 'PointsInPaint')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'PointsInPaint')}
      </p>
      <p class="text-gray-5">Points In Paint</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'PointsInPaint') >
        getValue(awayTeamStats ?? {}, 'PointsInPaint')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'PointsInPaint')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'SecondChancePoints') >
        getValue(homeTeamStats ?? {}, 'SecondChancePoints')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'SecondChancePoints')}
      </p>
      <p class="text-gray-5">Second Chance Points</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'SecondChancePoints') >
        getValue(awayTeamStats ?? {}, 'SecondChancePoints')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'SecondChancePoints')}
      </p>
    </div>
  </div>
</TeamPanel>

<TeamPanel>
  <div
    class="border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
  >
    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'PersonalFouls') <
        getValue(homeTeamStats ?? {}, 'PersonalFouls')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'PersonalFouls')}
      </p>
      <p class="text-gray-5">Fouls</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'PersonalFouls') <
        getValue(awayTeamStats ?? {}, 'PersonalFouls')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'PersonalFouls')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'TechnicalFouls') <
        getValue(homeTeamStats ?? {}, 'TechnicalFouls')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'TechnicalFouls')}
      </p>
      <p class="text-gray-5">Technical Fouls</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'TechnicalFouls') <
        getValue(awayTeamStats ?? {}, 'TechnicalFouls')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'TechnicalFouls')}
      </p>
    </div>

    <div class="flex justify-center relative">
      <p
        class="absolute top-1 left-1 px-2 rounded-2xl"
        style={getValue(awayTeamStats ?? {}, 'FlagrantFouls') <
        getValue(homeTeamStats ?? {}, 'FlagrantFouls')
          ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(awayTeamStats ?? {}, 'FlagrantFouls')}
      </p>
      <p class="text-gray-5">Flagrant Fouls</p>
      <p
        class="absolute top-1 right-1 px-2 rounded-2xl"
        style={getValue(homeTeamStats ?? {}, 'FlagrantFouls') <
        getValue(awayTeamStats ?? {}, 'FlagrantFouls')
          ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
          : undefined}
      >
        {getDisplay(homeTeamStats ?? {}, 'FlagrantFouls')}
      </p>
    </div>
  </div>
</TeamPanel>

{#if $plays.length > 0}
  <TeamPanel>
    <div
      class="border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
    >
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={biggestLead.away > biggestLead.home
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {biggestLead.away}
        </p>
        <p class="text-gray-5">Biggest Lead</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={biggestLead.home > biggestLead.away
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {biggestLead.home}
        </p>
      </div>

      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={longestRun.away > longestRun.home
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {longestRun.away}
        </p>
        <p class="text-gray-5">Longest Unanswered Run</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={longestRun.home > longestRun.away
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {longestRun.home}
        </p>
      </div>

      <div class="flex relative">
        <p class="px-2 rounded-2xl text-gray-5">Lead Changes</p>
        <p class="absolute top-1 right-1 px-2 rounded-2xl">
          {leadChanges.count}
        </p>
      </div>

      <div class="flex relative">
        <p class="px-2 rounded-2xl text-gray-5">Ties</p>
        <p class="absolute top-1 right-1 px-2 rounded-2xl">
          {ties}
        </p>
      </div>
    </div>
  </TeamPanel>
{/if}
